import { Share2, Facebook, Linkedin, MessageCircle, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";

interface ShareEventButtonProps {
  eventTitle: string;
  eventUrl: string;
  variant?: "default" | "outline" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
}

export const ShareEventButton = ({
  eventTitle,
  eventUrl,
  variant = "outline",
  size = "default",
  className,
}: ShareEventButtonProps) => {
  const encodedUrl = encodeURIComponent(eventUrl);
  const encodedTitle = encodeURIComponent(eventTitle);

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer.php?u=${encodedUrl}`,
    x: `https://x.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    whatsapp: `https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`,
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(eventUrl);
      toast.success("Link copied!");
    } catch (error) {
      toast.error("Copy failed");
    }
  };

  const handleShare = (platform: keyof typeof shareLinks) => {
    window.open(shareLinks[platform], "_blank", "width=600,height=400");
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant={variant} size={size} className={className}>
          <Share2 className="h-4 w-4" />
          {size !== "icon" && <span className="ml-2">Share</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64">
        <div className="space-y-2">
          <h4 className="font-semibold text-sm mb-3">Share this event</h4>

          <Button
            variant="ghost"
            className="w-full justify-start"
            onClick={handleCopyLink}
          >
            <Copy className="h-4 w-4 mr-2" />
            Copy Link
          </Button>

          <Button
            variant="ghost"
            className="w-full justify-start"
            onClick={() => handleShare("facebook")}
          >
            <Facebook className="h-4 w-4 mr-2" />
            Share on Facebook
          </Button>

          <Button
            variant="ghost"
            className="w-full justify-start"
            onClick={() => handleShare("x")}
          >
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
            Share on X
          </Button>

          <Button
            variant="ghost"
            className="w-full justify-start"
            onClick={() => handleShare("linkedin")}
          >
            <Linkedin className="h-4 w-4 mr-2" />
            Share on LinkedIn
          </Button>

          <Button
            variant="ghost"
            className="w-full justify-start"
            onClick={() => handleShare("whatsapp")}
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            Share on WhatsApp
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
